import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { projectServiceEndpoint } from "api/project";

import {
  type LineStyle,
  LineStyleSchema,
  ShapefileStyleProp,
  type ShapefileStylePropT,
  ShapefileStyleUpdateSchema
} from "components/project/shapefiles/shapefiles.constants";

import {
  ORGANIZATION_SHAPEFILES_QUERY_KEYS,
  PROJECT_SHAPEFILES_QUERY_KEYS,
  SYSTEM_SHAPEFILES_QUERY_KEYS
} from "../../queries";
import {
  SHAPEFILE_LIST_QUERY_KEYS,
  Shapefile
} from "../../queries/useShapefilesListQuery";

export interface IUseUpdateProjectShapefileMutationParams {
  onError?: (error) => void;
  onMutate?: () => void;
  onSettled?: () => void;
  onSuccess?: (data) => void;
  projectId: string;
}

export type UpdateProjectShapefileParamsT = {
  body: {
    key: ShapefileStylePropT;
    value: string | number | LineStyle;
  };
  projectId: string;
  shapefileId: string;
};

async function updateProjectShapefile(params: UpdateProjectShapefileParamsT) {
  const parsed = ShapefileStyleUpdateSchema.safeParse(params.body);
  if (!parsed.success) {
    throw new Error(`Invalid shapefile style update: ${parsed.error.message}`);
  }

  if (
    parsed.data.key === ShapefileStyleProp.LineStyle &&
    !LineStyleSchema.safeParse(parsed.data.value).success
  ) {
    throw new Error("Invalid lineStyle value");
  }

  const url =
    params.projectId === "system"
      ? `${projectServiceEndpoint}/system-shapefile/update/${params.shapefileId}`
      : `${projectServiceEndpoint}/shapefile/update/${params.shapefileId}`;
  return await axios.put(url, params.body);
}

export function useUpdateProjectShapefileMutation({
  onError,
  onMutate,
  onSettled,
  onSuccess,
  projectId
}: IUseUpdateProjectShapefileMutationParams) {
  const queryClient = useQueryClient();

  return useMutation(
    (params: UpdateProjectShapefileParamsT) => updateProjectShapefile({ ...params }),
    {
      onMutate: async (params: UpdateProjectShapefileParamsT) => {
        await queryClient.cancelQueries({
          queryKey: [SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList]
        });

        const previousData = queryClient.getQueriesData<{ data: Shapefile[] }>({
          queryKey: [SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList]
        });

        queryClient.setQueriesData<{ data: Shapefile[] }>(
          {
            predicate: (query) =>
              query.queryKey[0] === SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList
          },
          (oldData) => {
            if (!oldData?.data) return oldData;

            return {
              ...oldData,
              data: oldData.data.map((shapefile) => {
                if (
                  shapefile.shapefileId === params.shapefileId ||
                  shapefile.projectShapefileId === params.shapefileId
                ) {
                  return {
                    ...shapefile,
                    [params.body.key]: params.body.value,
                    features: shapefile.features.map((feature) => ({
                      ...feature,
                      [params.body.key]: params.body.value
                    }))
                  };
                }
                return shapefile;
              })
            };
          }
        );

        onMutate && onMutate();

        return { previousData };
      },
      onSuccess: (data, variables) => {
        if (variables.body.key === ShapefileStyleProp.Name) {
          if (projectId === "organization") {
            queryClient.invalidateQueries({
              queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
            });
          } else if (projectId === "system") {
            queryClient.invalidateQueries({
              queryKey: [SYSTEM_SHAPEFILES_QUERY_KEYS.systemShapefiles]
            });
          } else {
            queryClient.invalidateQueries({
              queryKey: [
                PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles,
                projectId
              ]
            });
          }
        }
        if (projectId === "organization") {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
          });
        }
        onSuccess && onSuccess(data);
      },
      onError: (error, variables, context) => {
        if (context?.previousData) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data);
          });
        }

        if (projectId === "organization") {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
          });
        } else if (projectId === "system") {
          queryClient.invalidateQueries({
            queryKey: [SYSTEM_SHAPEFILES_QUERY_KEYS.systemShapefiles]
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles, projectId]
          });
        }

        queryClient.invalidateQueries({
          queryKey: [SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList]
        });

        onError && onError(error);
      },
      onSettled: onSettled && onSettled
    }
  );
}
